import { useNavigate } from '@tanstack/react-router'
import { Loader } from 'lucide-react'
import { useEffect } from 'react'
import { Badge } from '~/elements'
import { useWebPageSource } from '~/features/knowledge-bases/hooks/useKnowledgeSource'
import { useWebSourceCrawl } from '~/features/knowledge-bases/hooks/useWebSourceCrawl'
import type { KnowledgeSource, KnowledgeSourceStatusType } from '../../types'
import { KnowledgeSourceCrawlStatus, KnowledgeSourceStatus } from './KnowledgeSourceCell'
import { KnowledgeSourceMobileCard } from './KnowledgeSourceMobileCard'

type WebPageSourceMobileCardProps = {
  dsId: string
  workspaceId: string
  botId: string
  kbId: string
  backendKbId?: string
}

export const WebPageSourceMobileCard = ({
  dsId,
  workspaceId,
  botId,
  kbId,
  backendKbId,
}: WebPageSourceMobileCardProps) => {
  const { data, fetchNextPage, hasNextPage, isFetching, title } = useWebPageSource({ workspaceId, botId, kbId, dsId })
  const navigate = useNavigate()

  const hasUnresolvedPages = hasNextPage && !isFetching
  const MAX_PAGES = 5
  const pages = data.pages.flatMap((page) => page.files)

  useEffect(() => {
    if (hasNextPage && data.pages.length < MAX_PAGES) {
      void fetchNextPage()
    }
  }, [hasNextPage, pages, data.pages.length, fetchNextPage])

  const updatedAt =
    pages
      .map((page) => page.updatedAt)
      .sort()
      .at(-1) ?? ''
  const size = pages.reduce((acc, page) => acc + (page.size ?? 0), 0)

  const faviconUrl = pages.find((page) => page.metadata?.['favicon'] != null)?.metadata?.['favicon']

  const getStatus = () => {
    for (const page of pages) {
      if (page.status.includes('pending')) {
        return 'indexing_pending'
      }
      if (page.status.includes('failed')) {
        return 'indexing_failed'
      }
      if (page.status.includes('completed')) {
        return 'indexing_completed'
      }
    }
    return 'unknown'
  }

  // Map the web page source to a KnowledgeSource object for display
  const knowledgeSource: KnowledgeSource = {
    id: dsId,
    botId: botId,
    key: title,
    url: '',
    size: size,
    contentType: 'text/html',
    tags: {
      title: title,
      dsType: 'web-page',
    },
    metadata: {
      favicon: faviconUrl,
    },
    createdAt: updatedAt,
    updatedAt: updatedAt,
    accessPolicies: ['public_content'],
    index: true,
    status: getStatus() as KnowledgeSourceStatusType,
    owner: {
      type: 'bot',
      id: botId,
    },
  }

  const additionalInfo = (
    <Badge variant="outline" color="blue" className="flex items-center gap-1 whitespace-nowrap text-xs">
      {isFetching && <Loader className="animate-[spin_2s_linear_infinite]" size={12} />}
      <span>
        {pages.length ?? 0}
        {hasUnresolvedPages && '+'} {pages.length === 1 ? 'page' : 'pages'}
      </span>
    </Badge>
  )

  const crawlTask = useWebSourceCrawl({ workspaceId, botId, kbId, dsId })

  if (crawlTask) {
    return (
      <KnowledgeSourceMobileCard
        knowledgeSource={knowledgeSource}
        onClick={() =>
          navigate({
            to: '/workspaces/$workspaceId/bots/$botId/knowledge-bases/$kbId/$dsId',
            params: { workspaceId, botId, kbId: backendKbId ?? kbId, dsId },
          })
        }
        status={<KnowledgeSourceCrawlStatus status={crawlTask.status ?? 'pending'} />}
        showOrigin={true}
        additionalInfo={additionalInfo}
      />
    )
  }

  return (
    <KnowledgeSourceMobileCard
      knowledgeSource={knowledgeSource}
      onClick={() =>
        navigate({
          to: '/workspaces/$workspaceId/bots/$botId/knowledge-bases/$kbId/$dsId',
          params: { workspaceId, botId, kbId: backendKbId ?? kbId, dsId },
        })
      }
      status={<KnowledgeSourceStatus status={knowledgeSource.status} />}
      showOrigin={true}
      additionalInfo={additionalInfo}
    />
  )
}
